/* Laserlab Shop: the treatment lists' controls (laserlab-list-controls.css) for the products this collection renders.
   One sticky row: «Etter <behandling>» (a chip to remove), Filter (opens the sheet), chosen brands as chips to remove,
   then the skin needs (one tap on, one tap off, one at a time), with the Sorter and search icons at the far right.
   The sheet chooses several needs and brands at once. The count sits under the list. The promo cards belong to the Shop's
   front page; on phones only the sign-up card, in the grid after the fourth product (with «Rolig Shop-topp» only that
   card, after the second row at every width). State lives in the address (?behov, ?merke, ?etter, ?sorter, ?sok and #category),
   so Back, reload and shared links show the same view.
   «Shop som butikk» (11.10, improvement butikk, .ll-shop--butikk): the row's chips are product types (several at once,
   ?type=), Filter and Sorter sit at its right end and it does not stick. The sheet holds Type, Behov and Merke (OR in a
   group, AND across groups). The «Etter <behandling>» views keep their need chips. The row has no search, so ?sok is
   dropped from the address. Without the class all as before. */

/* The filter sheet is the theme drawer (shared overlay, round close button, focus trap, Escape, scroll lock).
   It settles after its transition or a short fallback, so it also opens fully when transitions are off. */
(() => {
  const define = () => {
    const Drawer = customElements.get('drawer-component');
    if (!Drawer || customElements.get('ll-shop-sheet')) return;
    customElements.define('ll-shop-sheet', class extends Drawer {
      settle() {
        const overlay = this.querySelector('.fixed-overlay');
        const instant = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        return new Promise(resolve => {
          let done = false;
          const finish = () => { if (!done) { done = true; resolve(); } };
          overlay?.addEventListener('transitionend', finish, { once: true });
          window.setTimeout(finish, instant ? 40 : 450);
        });
      }
      handleShowTransition() {
        window.setTimeout(() => this.setAttribute('active', ''), 20);
        return this.settle();
      }
      handleHideTransition() {
        this.removeAttribute('active');
        return this.settle();
      }
    });
  };
  if (customElements.get('drawer-component')) define();
  else customElements.whenDefined('drawer-component').then(define);
})();

if (!customElements.get('laserlab-shop')) {
  customElements.define('laserlab-shop', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const q = selector => this.querySelector(selector);
      this.grid = q('[data-shop-grid]');
      this.sortSelect = q('[data-shop-sort]');
      if (!this.grid || !this.sortSelect) { this.removeAttribute('data-restoring'); return; }
      this.bar = q('[data-shop-bar]');
      this.butikk = this.classList.contains('ll-shop--butikk');
      this.kjop = this.classList.contains('ll-shop--kjop');
      this.chipsGroup = q('[data-shop-chips]');
      // «Shop som butikk»: the row's two labels (Etter type, and Etter behov on an «Etter <behandling>» view).
      this.rowLabels = Array.from(this.querySelectorAll('[data-shop-row-label]'));
      this.form = q('[data-filter-form]');
      this.input = q('[data-filter-input]');
      this.clear = q('[data-filter-clear]');
      this.hint = q('[data-filter-hint]');
      this.searchToggle = q('[data-filter-search-toggle]');
      this.searchLabel = this.input ? this.querySelector(`label[for="${this.input.id}"]`) : null;
      this.sortControl = q('[data-shop-sort-control]');
      this.sortCaption = q('[data-shop-sort-caption]');
      this.filterButton = q('[data-shop-filter-open]');
      this.filterBadge = q('[data-shop-filter-badge]');
      this.needChips = Array.from(this.querySelectorAll('[data-shop-need-chip]'));
      this.typeChips = Array.from(this.querySelectorAll('[data-shop-type-chip]'));
      this.etterChips = Array.from(this.querySelectorAll('[data-shop-etter]'));
      this.firstNeedChip = this.needChips[0] || null;
      this.count = q('[data-shop-count]');
      this.results = q('[data-shop-results]');
      this.empty = q('[data-filter-empty]');
      this.heading = q('[data-shop-heading]');
      // The page title without «Etter <behandling>» (the server gives it when the page opened in that view).
      this.baseTitle = this.dataset.baseTitle || document.title;
      this.description = q('[data-shop-description]');
      this.tabs = Array.from(this.querySelectorAll('.ll-tabs__link[data-shop-category]'));
      // «‹ Alle produkter» above the title: the way back from a category (it acts as the Alle tab).
      this.parentLink = q('[data-shop-parent]');
      this.promoSlot = q('[data-shop-promo-slot]');
      this.promoCards = Array.from(this.querySelectorAll('[data-shop-promo]'));
      // The sheet moves to <body> while it is open, so its parts are kept as references.
      this.sheet = q('[data-shop-sheet]');
      this.sheetNeeds = Array.from(this.sheet?.querySelectorAll('[data-shop-sheet-need]') || []);
      this.sheetNeedGroup = this.sheet?.querySelector('[data-shop-sheet-needs]');
      this.sheetTypes = Array.from(this.sheet?.querySelectorAll('[data-shop-sheet-type]') || []);
      this.sheetTypeGroup = this.sheet?.querySelector('[data-shop-sheet-types]');
      this.sheetBrandGroup = this.sheet?.querySelector('[data-shop-sheet-brands]');
      this.brandList = this.sheet?.querySelector('[data-shop-brand-options]');
      this.sheetReset = this.sheet?.querySelector('[data-shop-sheet-reset]');
      this.sheetApply = this.sheet?.querySelector('[data-shop-sheet-apply]');
      this.sidebar = q('[data-shop-sidebar]');
      this.desktopFilters = q('[data-shop-desktop-filters]');
      this.sidebarReset = q('[data-shop-sidebar-reset]');
      this.clearActive = q('[data-shop-clear-active]');
      this.filterBody = this.sheet?.querySelector('.ll-shop-sheet__body');
      this.filterInner = this.sheet?.querySelector('.ll-shop-sheet__inner');
      this.filterFooter = this.sheet?.querySelector('.ll-shop-sheet__footer');

      this.categoryLabels = { all: 'Alle', hudpleie: 'Hudpleie', tools: 'Tools', tilskudd: 'Tilskudd', gavekort: 'Gavekort' };
      this.categoryDescriptions = {
        all: this.kjop ? '' : 'Hudpleie, tools, tilskudd og gavekort fra Laserlab.',
        hudpleie: 'Produkter til den daglige hudpleierutinen.',
        tools: 'Utstyr til hudpleierutinen hjemme.',
        tilskudd: 'Tilskudd til hverdagen.',
        gavekort: 'Gi en gave fra Laserlab.'
      };
      this.sortCaptions = { bestselling: 'Bestselgere', newest: 'Nyheter', 'price-ascending': 'Laveste pris', 'price-descending': 'Høyeste pris', name: 'Navn A–Å' };
      // Ignoring punctuation and spaces keeps "Dr. Different" and "Dr.Different" titles together in Navn A–Å.
      this.collator = new Intl.Collator('nb-NO', { sensitivity: 'base', numeric: true, ignorePunctuation: true });
      // Need names come from the chips and the sheet (one source: snippets/laserlab-shop-need.liquid).
      this.needLabels = {};
      [...this.needChips, ...this.sheetNeeds].forEach(button => {
        const slug = button.dataset.shopNeedChip || button.dataset.shopSheetNeed;
        const label = button.querySelector('[data-shop-need-label], .ll-shop-option__label')?.textContent.trim();
        if (slug && label && !this.needLabels[slug]) this.needLabels[slug] = label;
      });
      // Type names in the row's order (one source: snippets/laserlab-shop-type.liquid).
      this.typeLabels = {};
      [...this.typeChips, ...this.sheetTypes].forEach(button => {
        const slug = button.dataset.shopTypeChip || button.dataset.shopSheetType;
        const label = button.querySelector('[data-shop-type-name], .ll-shop-option__label')?.textContent.trim();
        if (slug && label && !this.typeLabels[slug]) this.typeLabels[slug] = label;
      });
      // Etter behandling: the products each treatment category lists («Kategoriside: etterpleie»).
      this.etterLists = new Map(this.etterChips.map(chip => [chip.dataset.shopEtter, new Set((chip.dataset.ids || '').split(' ').filter(Boolean))]));
      // The list's own order (the team's ranking; a treatment page shows its first two), for the default sort.
      this.etterRanks = new Map(this.etterChips.map(chip => [chip.dataset.shopEtter, (chip.dataset.ids || '').split(' ').filter(Boolean)]));
      this.items = Array.from(this.grid.querySelectorAll('[data-shop-item]')).map((node, index) => {
        let tags = [];
        try { tags = JSON.parse(node.dataset.shopTags || '[]'); } catch (_) { /* Untagged products still match their visible information. */ }
        const tagLabels = Array.isArray(tags) ? tags.map(tag => String(tag).replace(/^tag__[^_]+_/, '')) : [];
        const family = node.dataset.shopFamily || '';
        const brand = node.dataset.shopBrand || '';
        const title = node.dataset.shopTitle || '';
        const needs = (node.dataset.shopNeeds || '').split(' ').filter(Boolean);
        const type = node.dataset.shopType || '';
        // The product's own words match anywhere (as before); data-search-words (the words people use for each need
        // and the product's own «Søkeord», snippets/laserlab-search-words.liquid) only from the start of a word, so
        // «ringer» in «mørke ringer» never finds «pigmentforandringer» (28.09).
        const own = [title, node.dataset.shopExcerpt, brand, this.categoryLabels[family], ...tagLabels, ...needs.map(need => this.needLabels[need] || ''), this.typeLabels[type] || ''].join(' ');
        const raw = `${own} ${node.dataset.searchWords || ''}`;
        const text = this.normalize(own);
        return {
          node, index, family, brand, title, needs, type, raw, text,
          id: node.dataset.shopId || '',
          words: this.normalize(raw).split(' '),
          price: Number(node.dataset.shopPrice) || 0,
          published: Number(node.dataset.shopPublished) || 0,
          soldout: node.hasAttribute('data-shop-soldout') ? 1 : 0
        };
      });
      this.orderKey = this.items.map(item => item.index).join(',');
      this.state = { category: 'all', needs: [], brands: [], types: [], etter: '', sort: 'bestselling', query: '' };
      // A brand collection (Dr. Different) is only a brand filter when it narrows what this page shows.
      const initialBrand = this.dataset.initialBrand || '';
      const initialCategory = this.dataset.initialCategory || 'all';
      this.initialBrand = initialBrand && this.items.some(item => this.inCategory(item, initialCategory) && item.brand !== initialBrand) ? initialBrand : '';
      this.pageOnly = Number(this.dataset.pageCount) > 1 ? ' på denne siden' : '';

      this.abort = new AbortController();
      const on = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: this.abort.signal });
      // «Rolig Shop-topp» (11.10): the sign-up band sits after the second row, so it follows the column count (2, 3, 4).
      ['(min-width: 768px)', '(min-width: 1200px)'].forEach(query => on(window.matchMedia(query), 'change', () => this.placePromo()));
      if (this.kjop) {
        this.desktopQuery = window.matchMedia('(min-width: 1024px)');
        on(this.desktopQuery, 'change', () => { this.syncFilterPlacement(); this.placePromo(); });
        on(this.sidebarReset, 'click', () => {
          this.state.needs = []; this.state.brands = []; this.state.types = []; this.commit();
          this.desktopFilters.scrollTop = 0;
          this.sidebar?.querySelector('.ll-shop-option:not([disabled])')?.focus({ preventScroll: true });
        });
      }

      // The row: one need at a time (a tap on another need switches); a chosen chip removes itself.
      this.needChips.forEach(chip => on(chip, 'click', () => {
        const need = chip.dataset.shopNeedChip;
        this.state.needs = this.state.needs.includes(need) ? this.state.needs.filter(item => item !== need) : [need];
        this.commit();
        this.afterChoice(chip);
      }));
      // «Shop som butikk»: a type chip adds or removes its type; several can be chosen (the sheet's Type group is the same).
      this.typeChips.forEach(chip => on(chip, 'click', () => {
        const type = chip.dataset.shopTypeChip;
        this.state.types = this.state.types.includes(type) ? this.state.types.filter(item => item !== type) : [...this.state.types, type];
        this.commit();
        this.afterChoice(chip);
      }));
      this.etterChips.forEach(chip => on(chip, 'click', () => {
        this.state.etter = '';
        // «Shop som butikk»: the row then holds types, so a need chosen in the «Etter <behandling>» row goes with it
        // instead of filtering on unseen.
        if (this.butikk) this.state.needs = [];
        this.commit();
        this.focusInRow();
        this.scrollToResults();
      }));
      on(this.bar, 'click', event => {
        const chip = event.target instanceof Element ? event.target.closest('[data-shop-brand-chip]') : null;
        if (!chip) return;
        this.state.brands = this.state.brands.filter(brand => brand !== chip.dataset.shopBrandChip);
        this.commit();
        this.focusInRow();
        this.scrollToResults();
      });
      // The sheet: several needs and brands at once.
      this.sheetTypes.forEach(button => on(button, 'click', () => {
        const type = button.dataset.shopSheetType;
        this.state.types = this.state.types.includes(type) ? this.state.types.filter(item => item !== type) : [...this.state.types, type];
        this.commit();
      }));
      this.sheetNeeds.forEach(button => on(button, 'click', () => {
        const need = button.dataset.shopSheetNeed;
        this.state.needs = this.state.needs.includes(need) ? this.state.needs.filter(item => item !== need) : [...this.state.needs, need];
        this.commit();
      }));
      on(this.brandList, 'click', event => {
        const button = event.target instanceof Element ? event.target.closest('[data-shop-sheet-brand]') : null;
        if (!button || button.disabled) return;
        const brand = button.dataset.shopSheetBrand;
        this.state.brands = this.state.brands.includes(brand) ? this.state.brands.filter(item => item !== brand) : [...this.state.brands, brand];
        this.commit();
      });
      on(this.sheetReset, 'click', () => {
        const focused = document.activeElement === this.sheetReset;
        this.state.needs = []; this.state.brands = []; this.state.types = []; this.commit();
        // «Shop som butikk»: Nullstill turns itself off, so focus moves on to «Vis N produkter» and stays in the sheet.
        if (this.butikk && focused && this.sheetReset.disabled) this.sheetApply?.focus();
      });
      // Wait for scroll unlock and focus return before showing the chosen products.
      on(this.sheetApply, 'click', () => {
        if (!this.kjop) { this.scrollToResults(); return; }
        this.sheet.addEventListener('drawer:handleAfterHide', () => {
          window.setTimeout(() => requestAnimationFrame(() => this.scrollToResults({ force: true })), 0);
        }, { once: true, signal: this.abort.signal });
      });
      on(this.sortSelect, 'change', () => {
        this.state.sort = Object.hasOwn(this.sortCaptions, this.sortSelect.value) ? this.sortSelect.value : 'bestselling';
        this.commit();
        this.scrollToResults();
      });

      if (this.input) {
        on(this.input, 'input', () => { this.state.query = this.input.value; this.commit({ quiet: true }); });
        on(this.input, 'search', () => { this.state.query = this.input.value; this.commit({ quiet: true }); });
        on(this.input, 'keydown', event => {
          if (event.key !== 'Escape' || !this.isSearchOpen()) return;
          event.preventDefault();
          event.stopPropagation();
          this.closeSearch();
        });
        on(this.form, 'submit', event => {
          event.preventDefault();
          this.state.query = this.input.value;
          this.commit();
          // Phones: Enter closes the keyboard so the results are visible.
          if (window.matchMedia('(max-width: 767px)').matches) this.input.blur();
        });
        on(this.clear, 'click', event => {
          event.preventDefault();
          if (this.isSearchOpen()) { this.closeSearch(); return; }
          this.input.value = '';
          this.state.query = '';
          this.commit();
          this.input.focus({ preventScroll: true });
        });
        on(this.searchToggle, 'click', () => this.openSearch());
        // An opened but empty search folds back when focus leaves it.
        on(this.form, 'focusout', () => {
          requestAnimationFrame(() => {
            if (!this.isSearchOpen() || this.form.contains(document.activeElement) || this.state.query.trim()) return;
            this.setSearchOpen(false);
            this.render();
          });
        });
        // The spelling help loads after the page; a pending search is tried again with it.
        on(document, 'laserlab:search-terms', () => { if (this.state.query.trim()) this.render(); });
      }
      on(this, 'click', event => {
        if (event.target instanceof Element && event.target.closest('[data-filter-reset]')) {
          this.clearFilters();
          this.focusInRow();
        }
      });
      // Category tabs on this page, and the same categories in the header's category bar (phones, tablets), keep the
      // other choices and add a history step (Back returns to the previous tab).
      const menuLinks = Array.from(document.querySelectorAll('a.ll-catbar__link[data-shop-menu-category]'));
      [...this.tabs, this.parentLink, ...menuLinks].filter(Boolean).forEach(tab => on(tab, 'click', event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        let target;
        try { target = new URL(tab.href, location.href); } catch (_) { return; }
        const key = tab.dataset.shopCategory || tab.dataset.shopMenuCategory;
        // The full catalogue can filter categories in place; hrefs remain usable without JavaScript or in a new tab.
        const giftCardChip = key === 'gavekort' && tab.hasAttribute('data-shop-category-chip') && this.scope(key, '').length > 0 && target.pathname.startsWith('/products/');
        const catalogCategory = this.kjop && this.dataset.collectionHandle === 'all' && Number(this.dataset.pageCount) <= 1 && (giftCardChip || ['hudpleie', 'tools', 'tilskudd'].includes(key) && target.pathname.startsWith('/collections/'));
        if (target.origin !== location.origin || (!catalogCategory && target.pathname !== location.pathname)) return;
        if (key !== 'all' && !catalogCategory && decodeURIComponent(target.hash.slice(1)).toLowerCase() !== key) return;
        event.preventDefault();
        // The category already shown: nothing to change, but from the bar deep in the list it still goes back to the title.
        const resetTypes = tab.hasAttribute('data-shop-category-chip') && key === 'all' && this.state.types.length > 0;
        if (key === this.state.category && this.hashKey() !== 'dr-different' && !resetTypes) {
          if (menuLinks.includes(tab)) this.scrollToHead();
          return;
        }
        // Keep a just-typed query when a category is picked before its 400 ms URL debounce.
        window.clearTimeout(this.writeTimer);
        this.writeState();
        const next = new URL(location.href);
        if (resetTypes) next.searchParams.delete('type');
        next.hash = key === 'all' && this.dataset.initialCategory === 'all' ? '' : key;
        history.pushState(history.state, '', next.href);
        this.readLocation();
        // The bar stays in view all the way down: a category picked there starts at its title, as if opened from the top.
        if (menuLinks.includes(tab)) this.scrollToHead();
      }));
      on(window, 'hashchange', () => this.readLocation());
      on(window, 'popstate', () => this.readLocation());
      on(window, 'pageshow', event => { if (event.persisted) this.readLocation(); else this.syncControls(); });

      // The chip row stays under the header while the list scrolls; once it is stuck it casts the header's shadow. A row
      // that scrolls with the page («Shop som butikk») is never stuck, so the header keeps its own shadow.
      if (this.bar) {
        const onScroll = () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            const style = getComputedStyle(this.bar);
            const top = parseFloat(style.top) || 0;
            this.bar.classList.toggle('is-stuck', style.position === 'sticky' && window.scrollY > 0 && this.bar.getBoundingClientRect().top <= top + .5);
          });
        };
        on(window, 'scroll', onScroll, { passive: true });
        on(window, 'resize', onScroll, { passive: true });
        onScroll();
      }

      this.initialized = true;
      this.readLocation();
      if (this.kjop) this.syncFilterPlacement();
      this.removeAttribute('data-restoring');
    }

    // Use the very same filter controls in a normal desktop aside and the mobile modal.
    // Moving only the body keeps one set of IDs, counts and selected values; the aside never traps focus.
    syncFilterPlacement() {
      if (!this.filterBody || !this.desktopFilters || !this.filterInner) return;
      const desktop = this.desktopQuery.matches;
      if (desktop && this.sheet.hasAttribute('open')) {
        if (!this.filterMovePending) {
          this.filterMovePending = true;
          this.sheet.addEventListener('drawer:handleAfterHide', () => {
            this.filterMovePending = false;
            this.syncFilterPlacement();
            if (this.desktopQuery.matches) this.sidebar?.querySelector('.ll-shop-option:not([disabled])')?.focus({ preventScroll: true });
          }, { once: true, signal: this.abort.signal });
          this.sheet.hide();
        }
        return;
      }
      const movedFocus = this.filterBody.contains(document.activeElement);
      if (desktop) {
        this.desktopFilters.append(this.filterBody);
        // Keep product types separate from the main category navigation.
        [this.sheetTypeGroup, this.sheetBrandGroup, this.sheetNeedGroup].filter(Boolean).forEach(group => this.filterBody.append(group));
      } else {
        [this.sheetTypeGroup, this.sheetNeedGroup, this.sheetBrandGroup].filter(Boolean).forEach(group => this.filterBody.append(group));
        this.filterInner.insertBefore(this.filterBody, this.filterFooter);
      }
      this.dataset.filterLayout = desktop ? 'desktop' : 'mobile';
      this.syncSidebar();
      if (!desktop && movedFocus) this.filterButton?.focus({ preventScroll: true });
    }

    syncSidebar() {
      if (!this.kjop || !this.sidebar) return;
      const shown = this.desktopQuery?.matches && [this.sheetTypeGroup, this.sheetBrandGroup, this.sheetNeedGroup].some(group => group && !group.hidden);
      this.sidebar.hidden = !shown;
      this.toggleAttribute('data-shop-sidebar-visible', Boolean(shown));
    }

    disconnectedCallback() {
      this.abort?.abort();
      // An editor reparent/reconnect must find the controls back in their original drawer.
      if (this.filterBody && this.filterInner && this.filterBody.parentElement === this.desktopFilters) {
        this.filterInner.insertBefore(this.filterBody, this.filterFooter);
      }
      this.filterMovePending = false;
      clearTimeout(this.writeTimer);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.initialized = false;
    }

    normalize(value) {
      return String(value).toLocaleLowerCase('nb-NO')
        .replace(/æ/g, 'ae').replace(/ø/g, 'o').normalize('NFD')
        .replace(/[̀-ͯ]/g, '').replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim();
    }

    hashKey() {
      try { return decodeURIComponent(location.hash.slice(1)).toLocaleLowerCase('nb-NO'); } catch (_) { return ''; }
    }

    // A word matches inside the product's own text, at the start of any of its words (search words included), or by
    // its stem ("rynker" -> "rynkebehandling"), like the site search.
    termMatches(item, term) {
      if (item.text.includes(term) || item.words.some(word => word.startsWith(term))) return true;
      const search = window.LaserlabSearchTerms;
      return Boolean(search) && search.stems(term).some(stem => item.words.some(word => search.stemMatches(word, stem)));
    }

    /* Search terms for the grid. When the typed words find nothing here, the site's spelling and language
       correction (assets/laserlab-search-terms.js) is tried against these products' own words. */
    searchTerms(pool) {
      const query = this.state.query.trim();
      const terms = this.normalize(query).split(' ').filter(Boolean);
      const search = window.LaserlabSearchTerms;
      if (!terms.length || !search || pool.some(item => terms.every(term => this.termMatches(item, term)))) return { terms, corrected: '' };
      if (!this.searchIndex) this.searchIndex = search.createIndex(this.items.map(item => item.raw));
      const correction = search.correct(query, this.searchIndex);
      if (!correction.changed) return { terms, corrected: '' };
      const corrected = this.normalize(correction.query).split(' ').filter(Boolean);
      return pool.some(item => corrected.every(term => this.termMatches(item, term))) ? { terms: corrected, corrected: correction.query } : { terms, corrected: '' };
    }

    inCategory(item, category = this.state.category) {
      return category === 'all' || item.family === category;
    }

    inEtter(item, etter = this.state.etter) {
      return !etter || Boolean(this.etterLists.get(etter)?.has(item.id));
    }

    scope(category = this.state.category, etter = this.state.etter) {
      return this.items.filter(item => this.inCategory(item, category) && this.inEtter(item, etter));
    }

    brandsFor(category, etter) {
      return Array.from(new Set(this.scope(category, etter).map(item => item.brand).filter(Boolean))).sort(this.collator.compare);
    }

    needsFor(category, etter) {
      const present = new Set(this.scope(category, etter).flatMap(item => item.needs));
      return Object.keys(this.needLabels).filter(need => present.has(need));
    }

    typesFor(category, etter) {
      const present = new Set(this.scope(category, etter).map(item => item.type).filter(Boolean));
      return Object.keys(this.typeLabels).filter(type => present.has(type));
    }

    // An «Etter <behandling>» view (?etter= here, or the etter page): its row keeps the need chips, also with butikk.
    isEtterView(etter = this.state.etter) {
      return Boolean(etter || this.dataset.etterPage);
    }

    listParam(params, name) {
      return Array.from(new Set(params.getAll(name).flatMap(value => value.split(',')).map(value => value.trim()).filter(Boolean)));
    }

    /* Reads category (#hash), behov, merke, etter, sorter, sok and (butikk) type from the address. Unknown values are dropped from it. */
    readLocation() {
      let params;
      try { params = new URLSearchParams(location.search); } catch (_) { params = new URLSearchParams(); }
      const key = this.hashKey();
      const initial = this.dataset.initialCategory || 'all';
      const category = key === 'dr-different' ? 'hudpleie' : Object.hasOwn(this.categoryLabels, key) ? key : initial;
      const etterKey = (params.get('etter') || '').toLocaleLowerCase('nb-NO');
      // «Etter <behandling>» stays while its products are in the category (a tab without them drops it).
      const etter = this.etterLists.has(etterKey) && this.items.some(item => this.inCategory(item, category) && this.inEtter(item, etterKey)) ? etterKey : '';
      let brands;
      if (key === 'dr-different') brands = ['Dr. Different'];
      else if (!params.has('merke')) brands = key && Object.hasOwn(this.categoryLabels, key) ? [] : (this.initialBrand ? [this.initialBrand] : []);
      else {
        const chosen = this.listParam(params, 'merke');
        brands = chosen.includes('alle') ? [] : chosen;
      }
      // Brand routes stay scoped even if this page has no matching product; other brands must exist in the view.
      const brandsHere = this.brandsFor(category, etter);
      brands = brands.filter(brand => brandsHere.includes(brand) || key === 'dr-different' || brand === this.initialBrand);
      const needsHere = this.needsFor(category, etter);
      const needs = this.listParam(params, 'behov').map(need => need.toLocaleLowerCase('nb-NO')).filter(need => needsHere.includes(need));
      const typesHere = this.butikk && !this.isEtterView(etter) ? this.typesFor(category, etter) : [];
      const types = this.listParam(params, 'type').map(type => type.toLocaleLowerCase('nb-NO')).filter(type => typesHere.includes(type));
      const sort = params.get('sorter') || 'bestselling';
      this.state = {
        category,
        brands,
        needs,
        types,
        etter,
        sort: Object.hasOwn(this.sortCaptions, sort) ? sort : 'bestselling',
        // Without the row's search («Shop som butikk») an old ?sok= link drops its word from the address instead of
        // filtering on a word nobody can see, as on the treatment lists.
        query: this.input ? params.get('sok') || '' : ''
      };
      this.dataset.category = category;
      // Another category starts its chip row from the beginning.
      if (this.shownCategory !== undefined && this.shownCategory !== category) {
        const row = this.chipsGroup || this.filterButton?.parentElement;
        if (row) row.scrollLeft = 0;
      }
      this.shownCategory = category;
      this.populateBrands();
      this.syncControls();
      this.render();
      this.writeState();
      this.updateNavigation();
      this.revealChip([...this.typeChips, ...this.needChips].find(chip => !chip.hidden && chip.getAttribute('aria-pressed') === 'true') || this.tabs.find(tab => tab.hasAttribute('data-shop-category-chip') && tab.hasAttribute('aria-current')));
    }

    /* Writes the state to the address without adding history steps (only category tabs do that). */
    writeState() {
      try {
        const url = new URL(location.href);
        const set = (name, value) => { if (value) url.searchParams.set(name, value); else url.searchParams.delete(name); };
        const same = (list, other) => list.length === other.length && list.every(item => other.includes(item));
        const { brands, needs, types, etter, sort, query, category } = this.state;
        const brandRoute = this.hashKey() === 'dr-different';
        let merke;
        if (brandRoute && same(brands, ['Dr. Different'])) merke = '';
        else if (this.initialBrand && same(brands, [this.initialBrand])) merke = '';
        else merke = brands.length ? brands.join(',') : (this.initialBrand ? 'alle' : '');
        set('behov', needs.join(','));
        if (this.butikk) set('type', types.join(','));
        set('merke', merke);
        set('etter', etter);
        set('sorter', sort === 'bestselling' ? '' : sort);
        set('sok', query.trim());
        if (brandRoute && !same(brands, ['Dr. Different'])) url.hash = category;
        if (url.href !== location.href) history.replaceState(history.state, '', url.href);
      } catch (_) { /* The filters still work without an addressable URL. */ }
    }

    /* Applies a state change: grid, counts, controls and the address. quiet = typing (address is updated after a pause). */
    commit({ quiet = false } = {}) {
      this.render();
      this.updateNavigation(); // heading, line and page title follow «Etter <behandling>» too
      window.clearTimeout(this.writeTimer);
      if (quiet) this.writeTimer = window.setTimeout(() => this.writeState(), 400);
      else this.writeState();
    }

    /* After a choice further down the list, the new result starts right under the sticky row. */
    afterChoice(chip) {
      this.revealChip(chip);
      this.scrollToResults();
    }

    scrollToResults({ force = false } = {}) {
      if (!this.results || !this.bar) return;
      const stickyTop = parseFloat(getComputedStyle(this.bar).top) || 0;
      if (this.kjop) {
        const target = Math.max(0, Math.round(this.results.getBoundingClientRect().top + window.scrollY - stickyTop - 16));
        if (force || window.scrollY > target + 1) window.scrollTo({ top: target, behavior: 'auto' });
        return;
      }
      if (!this.bar.classList.contains('is-stuck')) return;
      const gap = parseFloat(getComputedStyle(this.bar).marginBottom) || 0;
      const target = Math.round(this.results.getBoundingClientRect().top + window.scrollY - stickyTop - this.bar.offsetHeight - gap);
      if (window.scrollY > target + 1) window.scrollTo({ top: target, behavior: 'auto' });
    }

    /* The title band right under the sticky header. The chip row's sticky top resolves to --header-height even while
       the row is hidden (Tools, Tilskudd). Instant, upward only. */
    scrollToHead() {
      // «Shop som butikk» has no head box: the Shop's own top (the band, or the row on phones).
      const head = this.heading?.closest('.ll-shop__head') || (this.butikk ? this : null);
      if (!head) return;
      const headerHeight = parseFloat(getComputedStyle(this.bar || head).top) || 0;
      const target = Math.round(head.getBoundingClientRect().top + window.scrollY - headerHeight);
      if (window.scrollY > target + 1) window.scrollTo({ top: target, behavior: 'auto' });
    }

    // Keeps a chip fully in view in the swipe row without moving the page.
    revealChip(chip) {
      const list = chip?.parentElement;
      if (!chip || !list || chip.hidden || list.scrollWidth <= list.clientWidth) return;
      const chipBox = chip.getBoundingClientRect();
      const listBox = list.getBoundingClientRect();
      // The row's edge fade (--ll-reveal-gap, 32 px with rolig_filterrad, 11.10): a chosen chip stops clear of it.
      // Without the variable the gap is 0, the reveal as before.
      const fade = parseFloat(getComputedStyle(list).getPropertyValue('--ll-reveal-gap')) || 0;
      if (chipBox.left < listBox.left + fade) list.scrollLeft -= listBox.left - chipBox.left + 16 + fade;
      else if (chipBox.right > listBox.right - fade) list.scrollLeft += chipBox.right - listBox.right + 16 + fade;
    }

    // After a chip removes itself, focus stays in the row: the first chip that is still there, else search.
    focusInRow() {
      const target = [this.filterButton, ...this.tabs.filter(tab => tab.hasAttribute('data-shop-category-chip')), ...this.typeChips, ...this.needChips, this.searchToggle, this.input].find(element => element && !element.hidden && !element.disabled && element.getClientRects().length);
      this.revealChip(target);
      target?.focus({ preventScroll: true });
    }

    clearFilters() {
      this.state = { ...this.state, needs: [], brands: [], types: [], etter: '', query: '' };
      if (this.input) this.input.value = '';
      this.setSearchOpen(false);
      this.commit();
    }

    /* Controls follow the state (also after the browser restores form fields on Back or reload). */
    syncControls() {
      this.sortSelect.value = this.state.sort;
      if (this.input && this.input.value !== this.state.query) this.input.value = this.state.query;
      if (this.state.query.trim()) this.setSearchOpen(true);
      else if (document.activeElement !== this.input) this.setSearchOpen(false);
    }

    /* ---------- Search at the far right of the row: a tap turns the row into the field ---------- */

    isSearchOpen() {
      return Boolean(this.bar?.classList.contains('is-searching'));
    }

    setSearchOpen(open) {
      if (!this.bar || !this.searchToggle) return;
      this.bar.classList.toggle('is-searching', open);
      this.searchToggle.setAttribute('aria-expanded', String(open));
    }

    openSearch() {
      this.setSearchOpen(true);
      this.render();
      this.input?.focus({ preventScroll: true });
    }

    closeSearch() {
      if (this.input) this.input.value = '';
      this.state.query = '';
      this.setSearchOpen(false);
      this.commit();
      if (this.searchToggle?.getClientRects().length) this.searchToggle.focus({ preventScroll: true });
    }

    populateBrands() {
      if (!this.brandList) return;
      const brands = this.brandsFor(this.state.category, this.state.etter);
      this.state.brands.forEach(brand => { if (!brands.includes(brand)) brands.push(brand); });
      const key = brands.join('|');
      if (this.brandKey === key) return;
      this.brandKey = key;
      this.brandList.replaceChildren(...brands.map(brand => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'll-shop-option';
        button.dataset.shopSheetBrand = brand;
        button.setAttribute('aria-pressed', 'false');
        button.innerHTML = '<svg class="ll-shop-option__check" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m3.5 8.4 2.9 2.9 6.1-6.6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="ll-shop-option__label"></span><span class="ll-shop-option__count" data-shop-option-count></span>';
        button.querySelector('.ll-shop-option__label').textContent = brand;
        item.append(button);
        return item;
      }));
    }

    // Chosen brands as chips to remove, after Filter and before the needs (same look as a chosen need). With «Shop som
    // butikk» the row holds only types, so brands show there only on an «Etter <behandling>» view (as before); the
    // Filter badge counts them.
    renderBrandChips() {
      const brands = this.kjop || !this.butikk || this.isEtterView() ? this.state.brands : [];
      const key = brands.join('|');
      if (this.brandChipKey === key) return;
      this.brandChipKey = key;
      this.bar?.querySelectorAll('[data-shop-brand-chip]').forEach(chip => chip.remove());
      const row = this.chipsGroup || this.firstNeedChip?.parentElement || this.filterButton?.parentElement;
      if (!row) return;
      brands.forEach(brand => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'll-chip ll-chip--type';
        chip.dataset.shopBrandChip = brand;
        chip.setAttribute('aria-pressed', 'true');
        chip.setAttribute('aria-controls', this.grid.id);
        chip.innerHTML = '<span class="ll-chip__text"><span class="visually-hidden">Merke: </span><span class="ll-chip__label"></span></span><svg class="ll-chip__remove" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m4.5 4.5 7 7m0-7-7 7" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
        chip.querySelector('.ll-chip__label').textContent = brand;
        row.insertBefore(chip, this.firstNeedChip && this.firstNeedChip.parentElement === row ? this.firstNeedChip : null);
      });
    }

    updateNavigation() {
      const category = this.state.category;
      this.tabs.forEach(tab => {
        if (tab.dataset.shopCategory === category && !(tab.dataset.shopCategoryChip === 'all' && this.state.types.length)) tab.setAttribute('aria-current', 'page');
        else tab.removeAttribute('aria-current');
      });
      if (this.parentLink) this.parentLink.hidden = category === 'all' && this.dataset.collectionHandle === 'all';
      document.querySelectorAll('[data-shop-menu-category]').forEach(link => {
        if (link.dataset.shopMenuCategory === category && !this.state.etter) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
      const etterNow = this.state.etter || this.dataset.etterPage || '';
      this.toggleAttribute('data-etter-view', Boolean(this.state.etter)); // the promo row belongs to the front page only
      document.querySelectorAll('[data-shop-menu-etter]').forEach(link => {
        if (etterNow && link.dataset.shopMenuEtter === etterNow) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
      // Title and line follow the tab (the page's own title while it shows its own products). «Etter <behandling>» has
      // its own heading, line and page title, from the category's «etterpleie, overskrift» and «etterpleie, tekst».
      const key = this.hashKey();
      const brandRoute = key === 'dr-different' && this.state.brands.includes('Dr. Different');
      const ownView = !Object.hasOwn(this.categoryLabels, key) && !brandRoute;
      const etterChip = this.state.etter ? this.etterChips.find(chip => chip.dataset.shopEtter === this.state.etter) : null;
      const pageTitle = etterChip?.dataset.etterTitle || this.baseTitle;
      if (pageTitle && document.title !== pageTitle) document.title = pageTitle;
      if (etterChip?.dataset.etterHeading) {
        if (this.heading) this.heading.textContent = etterChip.dataset.etterHeading;
        if (this.description) this.description.textContent = etterChip.dataset.etterText || '';
      } else {
        if (this.heading) this.heading.textContent = brandRoute ? 'Dr. Different' : ownView || category === this.dataset.initialCategory ? this.dataset.initialHeading : category === 'all' ? 'Shop' : this.categoryLabels[category];
        if (this.description) this.description.textContent = brandRoute ? 'Hudpleie fra Dr. Different.' : ownView ? this.dataset.initialDescription : this.categoryDescriptions[category];
      }
      const scope = Number(this.dataset.pageCount) > 1 ? 'Søk på denne siden' : category === 'all' ? 'Søk i nettbutikken' : `Søk i ${this.categoryLabels[category]}`;
      if (this.searchLabel) this.searchLabel.textContent = scope;
      this.searchToggle?.setAttribute('aria-label', scope);
      this.form?.setAttribute('aria-label', scope);
    }

    /* Bestselgere keeps the page order (best sellers first); «Etter <behandling>» keeps the category's own ranking, so the
       two a treatment page shows come first. Nyheter, prices and names sort the whole grid. Sold-out products come last
       in every order (11.10; the page order already has them last). */
    order() {
      const order = this.state.sort;
      const ranked = order === 'bestselling' && this.state.etter ? this.etterRanks.get(this.state.etter) : null;
      const rank = item => { const at = ranked ? ranked.indexOf(item.id) : -1; return at < 0 ? Number.MAX_SAFE_INTEGER : at; };
      const sorted = [...this.items].sort((a, b) => {
        if (a.soldout !== b.soldout) return a.soldout - b.soldout;
        if (ranked) return rank(a) - rank(b) || a.index - b.index;
        if (order === 'newest') return b.published - a.published || a.index - b.index;
        if (order === 'name') return this.collator.compare(a.title, b.title) || a.index - b.index;
        if (order === 'price-ascending') return a.price - b.price || a.index - b.index;
        if (order === 'price-descending') return b.price - a.price || a.index - b.index;
        return a.index - b.index;
      });
      const key = sorted.map(item => item.index).join(',');
      if (key === this.orderKey) return;
      this.orderKey = key;
      sorted.forEach(item => this.grid.appendChild(item.node));
    }

    /* Phones: the sign-up card (link #nyhetsbrev) in the grid after the fourth product, on the Shop's front page only.
       It steps aside while choices narrow the list. From tablets up the cards sit in their own row (CSS), also only on
       the front page. With «Rolig Shop-topp» (11.10): only that card, after the second row at every width (2, 3 or 4
       columns). */
    placePromo(filtered = this.promoFiltered) {
      this.promoFiltered = filtered;
      const front = this.state.category === 'all' && !this.state.etter && this.dataset.collectionHandle === 'all' && !this.dataset.etterPage;
      const card = front ? this.promoCards.find(promo => promo.hasAttribute('data-promo-mobile')) : null;
      const visible = Array.from(this.grid.querySelectorAll('[data-shop-item]:not([hidden])'));
      const calm = this.butikk || (document.body?.dataset.llFlags || '').split(/\s+/).includes('rolig_shop');
      const after = calm ? 2 * Math.max(1, getComputedStyle(this.grid).gridTemplateColumns.split(' ').filter(Boolean).length) : 4;
      if (!card || filtered || visible.length <= after) {
        if (this.promoSlot) this.promoSlot.hidden = true;
        return;
      }
      if (!this.promoSlot) {
        this.promoSlot = document.createElement('div');
        this.promoSlot.setAttribute('role', 'listitem');
        this.promoSlot.dataset.shopPromoSlot = '';
      }
      if (this.promoSlot.dataset.promoId !== card.dataset.promoId || !this.promoSlot.firstElementChild) {
        const link = card.querySelector('.ll-shop-promo__link')?.cloneNode(true);
        if (!link) { this.promoSlot.hidden = true; return; }
        link.querySelectorAll('img').forEach(image => { image.loading = 'lazy'; image.removeAttribute('fetchpriority'); });
        this.promoSlot.className = `ll-shop__promo-slot ${card.className}`;
        this.promoSlot.dataset.promoId = card.dataset.promoId || '';
        this.promoSlot.replaceChildren(link);
      }
      this.promoSlot.hidden = false;
      // Without the switch: after the fourth product, phones only. With it: after the second row at every width, where
      // the CSS (grid-row: 3) already draws it, so moving it only keeps the Tab order in step with the picture.
      const anchor = visible[after - 1];
      if (anchor.nextElementSibling !== this.promoSlot) anchor.after(this.promoSlot);
    }

    label(count) {
      return `${count} ${count === 1 ? 'produkt' : 'produkter'}`;
    }

    setCount(node, count) {
      if (!node) return;
      const text = String(count);
      if (node.firstChild?.nodeType === Node.TEXT_NODE) { if (node.firstChild.nodeValue !== text) node.firstChild.nodeValue = text; }
      else node.prepend(text);
      const spoken = node.querySelector('.visually-hidden');
      if (spoken) spoken.textContent = ` ${count === 1 ? 'produkt' : 'produkter'}`;
    }

    render() {
      const { needs, brands, etter, types } = this.state;
      const query = this.state.query.trim();
      const inCategory = this.items.filter(item => this.inCategory(item));
      const scope = inCategory.filter(item => this.inEtter(item));
      const matchesBrand = item => !brands.length || brands.includes(item.brand);
      const matchesNeed = item => !needs.length || needs.some(need => item.needs.includes(need));
      // «Shop som butikk»: types are OR within the group, AND with needs and brands (always true without butikk).
      const matchesType = item => !types.length || types.includes(item.type);
      const { terms, corrected } = this.searchTerms(scope.filter(item => matchesBrand(item) && matchesNeed(item) && matchesType(item)));
      const matchesQuery = item => terms.every(term => this.termMatches(item, term));
      let matches = 0;
      this.items.forEach(item => {
        const visible = this.inCategory(item) && this.inEtter(item) && matchesBrand(item) && matchesNeed(item) && matchesType(item) && matchesQuery(item);
        item.node.hidden = !visible;
        if (visible) matches += 1;
      });

      // Choices, Sorter and search from four products in the view (as on the treatment lists), or while in use.
      // A row of needs is a choice from two needs up.
      const enough = scope.length > 3;
      const needsHere = new Set(scope.flatMap(item => item.needs));
      const needGroupShown = (enough && needsHere.size >= 2) || needs.length > 0;
      // «Shop som butikk»: the row shows the types present in the view (two or more), except on an «Etter <behandling>»
      // view, which keeps its need chips; there the needs stay in the sheet only.
      const etterView = this.isEtterView();
      const typesHere = new Set(scope.map(item => item.type).filter(Boolean));
      const typeGroupShown = this.butikk && !etterView && ((enough && typesHere.size >= 2) || types.length > 0);
      const needRow = !this.butikk || etterView;
      // Counts per choice answer "what do I get if I choose this", given the other choices.
      const typePool = scope.filter(item => matchesBrand(item) && matchesNeed(item) && matchesQuery(item));
      const typeCount = slug => typePool.filter(item => item.type === slug).length;
      this.typeChips.forEach(chip => {
        const slug = chip.dataset.shopTypeChip;
        const count = typeCount(slug);
        const pressed = types.includes(slug);
        chip.setAttribute('aria-pressed', String(pressed));
        chip.hidden = !pressed && (!typeGroupShown || !typesHere.has(slug));
        chip.disabled = !pressed && count === 0;
        this.setCount(chip.querySelector('[data-shop-chip-count]'), count);
      });
      this.sheetTypes.forEach(button => {
        const slug = button.dataset.shopSheetType;
        const count = typeCount(slug);
        const pressed = types.includes(slug);
        button.setAttribute('aria-pressed', String(pressed));
        button.disabled = !pressed && count === 0;
        button.closest('[data-shop-sheet-type-item]').hidden = !pressed && !typesHere.has(slug);
        button.querySelector('[data-shop-option-count]').textContent = count;
        button.setAttribute('aria-label', `${this.typeLabels[slug] || slug}, ${this.label(count)}`);
      });
      if (this.sheetTypeGroup) this.sheetTypeGroup.hidden = !typeGroupShown;
      const needPool = scope.filter(item => matchesBrand(item) && matchesType(item) && matchesQuery(item));
      const needCount = slug => needPool.filter(item => item.needs.includes(slug)).length;
      this.needChips.forEach(chip => {
        const slug = chip.dataset.shopNeedChip;
        const count = needCount(slug);
        const pressed = needs.includes(slug);
        chip.setAttribute('aria-pressed', String(pressed));
        chip.hidden = !needRow || (!pressed && (!needGroupShown || !needsHere.has(slug)));
        chip.disabled = !pressed && count === 0;
        this.setCount(chip.querySelector('[data-shop-chip-count]'), count);
      });
      this.sheetNeeds.forEach(button => {
        const slug = button.dataset.shopSheetNeed;
        const count = needCount(slug);
        const pressed = needs.includes(slug);
        button.setAttribute('aria-pressed', String(pressed));
        button.disabled = !pressed && count === 0;
        button.closest('[data-shop-sheet-need-item]').hidden = !pressed && !needsHere.has(slug);
        button.querySelector('[data-shop-option-count]').textContent = count;
        button.setAttribute('aria-label', `${this.needLabels[slug] || slug}, ${this.label(count)}`);
      });
      if (this.sheetNeedGroup) this.sheetNeedGroup.hidden = !needGroupShown;

      this.populateBrands();
      const brandPool = scope.filter(item => matchesNeed(item) && matchesType(item) && matchesQuery(item));
      const brandButtons = Array.from(this.brandList?.querySelectorAll('[data-shop-sheet-brand]') || []);
      brandButtons.forEach(button => {
        const value = button.dataset.shopSheetBrand;
        const count = brandPool.filter(item => item.brand === value).length;
        const pressed = brands.includes(value);
        button.setAttribute('aria-pressed', String(pressed));
        button.disabled = !pressed && count === 0;
        button.querySelector('[data-shop-option-count]').textContent = count;
        button.setAttribute('aria-label', `${value}, ${this.label(count)}`);
      });
      const brandGroupShown = (enough && brandButtons.length >= 2) || brands.length > 0;
      if (this.sheetBrandGroup) this.sheetBrandGroup.hidden = !brandGroupShown;
      this.syncSidebar();

      // Filter opens the sheet when it has something to choose; the badge counts the needs, brands and types chosen there.
      const chosen = needs.length + brands.length + types.length;
      if (this.filterButton) {
        this.filterButton.hidden = !needGroupShown && !brandGroupShown && !typeGroupShown;
        this.filterButton.setAttribute('aria-label', chosen ? `Filter, ${chosen} ${chosen === 1 ? 'valg aktivt' : 'valg aktive'}` : 'Filter');
      }
      if (this.filterBadge) {
        this.filterBadge.hidden = chosen === 0;
        this.filterBadge.textContent = String(chosen);
      }
      this.etterChips.forEach(chip => {
        const active = chip.dataset.shopEtter === etter;
        chip.hidden = !active;
        chip.setAttribute('aria-pressed', String(active));
      });
      this.renderBrandChips();

      if (this.sortControl) this.sortControl.hidden = !enough && this.state.sort === 'bestselling';
      if (this.form) this.form.hidden = !this.kjop && !enough && !query;
      // «Shop som butikk» has no search in the row: the row goes when it has no chip, no Filter and no Sorter.
      const formHidden = this.form ? this.form.hidden : true;
      const chipsShown = [this.filterButton, this.querySelector('[data-shop-etter-link]'), ...this.etterChips, ...this.typeChips, ...this.needChips, ...this.bar?.querySelectorAll('[data-shop-brand-chip]') || []].some(chip => chip && !chip.hidden);
      if (this.bar) {
        const rowHasChips = [...this.etterChips, ...this.typeChips, ...this.needChips, ...this.bar.querySelectorAll('[data-shop-category-chip], [data-shop-brand-chip]')].some(chip => !chip.hidden);
        this.bar.hidden = this.kjop ? !rowHasChips : !chipsShown && this.sortControl?.hidden && formHidden;
        this.bar.classList.toggle('ll-tfilter--search-only', !chipsShown && !formHidden);
      }
      // The row's label names what its chips choose; the chip group is named by it.
      if (this.rowLabels.length) {
        const label = this.rowLabels.find(node => node.dataset.shopRowLabel === (etterView ? 'behov' : 'type'));
        this.rowLabels.forEach(node => { node.hidden = node !== label; });
        if (label && this.chipsGroup && this.chipsGroup.getAttribute('aria-labelledby') !== label.id) this.chipsGroup.setAttribute('aria-labelledby', label.id);
      }

      this.order();
      const filtered = Boolean(needs.length || brands.length || types.length || etter || query);
      if (this.clearActive) this.clearActive.hidden = !filtered;
      this.placePromo(filtered);

      const total = inCategory.length;
      if (this.count) {
        this.count.textContent = filtered ? `${matches} av ${this.label(total)}${this.pageOnly}` : `${this.label(total)}${this.pageOnly}`;
        if (corrected) {
          const spoken = document.createElement('span');
          spoken.className = 'visually-hidden';
          spoken.textContent = `, treff for ${corrected}`;
          this.count.append(spoken);
        }
      }
      if (this.hint) {
        this.hint.textContent = corrected ? `Viser treff for «${corrected}».` : '';
        this.hint.toggleAttribute('data-correction', Boolean(corrected));
      }
      if (this.sheetReset) this.sheetReset.disabled = chosen === 0;
      if (this.sidebarReset) this.sidebarReset.disabled = chosen === 0;
      if (this.sheetApply) this.sheetApply.textContent = matches ? `Vis ${this.label(matches)}` : 'Ingen treff';
      if (this.empty) this.empty.hidden = matches !== 0;
      if (this.clear) {
        const open = this.isSearchOpen();
        this.clear.hidden = !this.input.value && !open;
        this.clear.setAttribute('aria-label', open ? 'Lukk og tøm søket' : 'Tøm søket');
      }
      if (this.sortCaption) this.sortCaption.textContent = this.sortCaptions[this.state.sort];
      // The sort icon in the row is filled while a sort other than Bestselgere is chosen.
      this.sortControl?.toggleAttribute('data-active', this.state.sort !== 'bestselling');
      // «Shop som butikk»: phones hide the chosen order's name, so a dot after «Sorter» says one is chosen.
      if (this.butikk) this.sortControl?.toggleAttribute('data-sort-active', this.state.sort !== 'bestselling');
    }
  });
}
